import { IOAuthProvider, OAuthTokens, RawUserInfo, NormalizedUserInfo, OAuthProviderConfig } from './types';

/**
 * Base class for all OAuth providers
 * Provides common OAuth flow logic and helper methods
 */
export abstract class BaseOAuthProvider implements IOAuthProvider {
  readonly id: string;
  readonly name: string;
  readonly clientId: string;
  readonly scopes: string[];
  readonly authorizationEndpoint: string;
  readonly tokenEndpoint: string;
  readonly userInfoEndpoint: string;

  constructor(config: OAuthProviderConfig) {
    this.id = config.id;
    this.name = config.name;
    this.clientId = config.clientId;
    this.scopes = config.scopes;
    this.authorizationEndpoint = config.authorizationEndpoint;
    this.tokenEndpoint = config.tokenEndpoint;
    this.userInfoEndpoint = config.userInfoEndpoint;
  }

  /**
   * Generate a random state token for CSRF protection
   */
  protected generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate PKCE code verifier
   */
  protected generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate PKCE code challenge from verifier
   */
  protected async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Build authorization URL - to be implemented by subclasses
   */
  abstract buildAuthorizationUrl(redirectUri: string, state: string, codeVerifier?: string): string;

  /**
   * Exchange authorization code for tokens - to be implemented by subclasses
   */
  abstract exchangeCodeForTokens(
    code: string,
    redirectUri: string,
    codeVerifier?: string
  ): Promise<OAuthTokens>;

  /**
   * Fetch user info from provider - to be implemented by subclasses
   */
  abstract fetchUserInfo(accessToken: string): Promise<RawUserInfo>;

  /**
   * Map provider-specific metadata to normalized format - to be implemented by subclasses
   */
  abstract mapMetadata(rawInfo: RawUserInfo): NormalizedUserInfo;

  /**
   * Helper to build query string from params
   */
  protected buildQueryString(params: Record<string, string | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });
    return searchParams.toString();
  }

  /**
   * Helper to split full name into first and last name
   */
  protected splitFullName(fullName: string): { firstName: string; lastName: string } {
    if (!fullName || !fullName.trim()) {
      return { firstName: '', lastName: '' };
    }

    const parts = fullName.trim().split(/\s+/);
    
    if (parts.length === 0) {
      return { firstName: '', lastName: '' };
    }
    
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    
    if (parts.length === 2) {
      return { firstName: parts[0], lastName: parts[1] };
    }
    
    // Multiple words - first word is first name, rest is last name
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' '),
    };
  }
}
